import { useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { hasPermission, canSeeFinancials, type Permission, type UserRole } from '@bstock/shared';

/**
 * Hook to check if the current user has a specific permission.
 * Returns false if user is not authenticated.
 *
 * @param permission - The permission to check
 * @returns boolean indicating if user has the permission
 *
 * @example
 * const canValidate = usePermission('inventories.validate');
 * {canValidate && <Button onClick={handleValidate}>Valider</Button>}
 */
export function usePermission(permission: Permission): boolean {
  const { user } = useAuth();

  return useMemo(() => {
    if (!user) return false;

    // SuperAdmin bypasses all permission checks
    if (user.isSuperAdmin) return true;

    const role = user.currentBrand?.bstockRole ?? user.currentBrand?.role;
    if (!role) return false;

    return hasPermission(role, permission);
  }, [user, permission]);
}

/**
 * Hook to check multiple permissions at once.
 * Returns an object with permission keys mapped to booleans.
 *
 * @param permissions - Array of permissions to check
 * @returns Record with permission keys and boolean values
 *
 * @example
 * const perms = usePermissions(['inventories.create', 'inventories.validate']);
 * {perms['inventories.create'] && <Button>Creer</Button>}
 */
export function usePermissions(permissions: Permission[]): Record<Permission, boolean> {
  const { user } = useAuth();

  return useMemo(() => {
    const result = {} as Record<Permission, boolean>;

    for (const permission of permissions) {
      if (!user) {
        result[permission] = false;
        continue;
      }

      // SuperAdmin bypasses all permission checks
      if (user.isSuperAdmin) {
        result[permission] = true;
        continue;
      }

      const role = user.currentBrand?.bstockRole ?? user.currentBrand?.role;
      if (!role) {
        result[permission] = false;
        continue;
      }

      result[permission] = hasPermission(role, permission);
    }

    return result;
  }, [user, permissions]);
}

/**
 * Shortcut hook to check if user can see financial data (PMP, costs, values).
 * Uses the 'stock.read_financials' permission.
 *
 * @returns boolean indicating if user can see financial data
 *
 * @example
 * const showPrices = useCanSeeFinancials();
 * {showPrices && <Column dataField="pmp" />}
 */
export function useCanSeeFinancials(): boolean {
  const { user } = useAuth();

  return useMemo(() => {
    if (!user) return false;

    // SuperAdmin can always see financials
    if (user.isSuperAdmin) return true;

    const role = user.currentBrand?.bstockRole ?? user.currentBrand?.role;
    if (!role) return false;

    return canSeeFinancials(role);
  }, [user]);
}

/**
 * Hook to get the current user's role.
 *
 * @returns UserRole or null if not authenticated
 *
 * @example
 * const role = useCurrentRole();
 * <Badge>{role}</Badge>
 */
export function useCurrentRole(): UserRole | null {
  const { user } = useAuth();

  return useMemo(() => {
    if (!user) return null;
    return user.currentBrand?.bstockRole ?? user.currentBrand?.role ?? null;
  }, [user]);
}
